import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import cloneDeep from 'lodash.cloneDeep';
import moment from 'moment';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ProgramEnrollmentSigningData } from '../../program-enrollments-signing/models/program-enrollment-signing.model';
import { RequestHeader } from '../../shared/enums';
import { CompensationTrigger } from '../../shared/enums/compensation-trigger.enum';
import { BaseHttpService } from '../../shared/services/base-http.service';
import { HttpConcurrencyClient } from '../../shared/services/http-concurrency-client.service';
import { textEncodeBase64 } from '../../utils/encode';
import { FolderDetails } from '../models/folder-details.model';
import { IncentiveProgramSigningErrorResponse } from '../models/incentive-program-error-response.model';
import {
  IncentiveProgramSigning, IncentiveProgramSigningValidationDataViewModel
} from '../models/incentive-program-signing.model';
import {
  LinkedTemplate, PostTemplateDocumentTypesRequest, TemplateDocumentDetails, TemplateDocumentType
} from '../models/linked-template.model';
import { TemplateDetails } from "../models/template-details.model";
import { DataFieldDefinition, DocumentFieldsResponse, DocumentFieldsValidationResponse } from '../models/template-document-fields.model';
import {
  TemplateSummary, TemplateSummaryLinkUnlink, TemplateSummaryLinkUnlinkRequest, TemplateSummaryList
} from '../models/template-summary.model';
import {
  encodeTextFields,
  mapTemplateDocumentDetailsToTemplateDocumentDocumentTypes, mapTemplateSummariesToTemplateSummaryLinkUnlink
} from '../utils/helpers';

@Injectable({
  providedIn: 'root'
})
export class IncentiveProgramsSigningService extends BaseHttpService<IncentiveProgramSigning> {
  documentTypes$ = new BehaviorSubject<TemplateDocumentType[]>(null);
  private readonly http: HttpClient;

  constructor(private httpConcurrencyClient: HttpConcurrencyClient) {
    super(httpConcurrencyClient.http);
    this.http = httpConcurrencyClient.http;
    this.apiDomain = environment.membershipApiUrl;
    this.endpoint = 'signing/programs';
  }

  getById(id: string): Observable<IncentiveProgramSigning> {
    return this.httpConcurrencyClient.getWithETag<IncentiveProgramSigning>(`${this.apiDomain}/api/${this.endpoint}/${id}`,
      id,
      { [RequestHeader.CacheControl]: 'no-cache', [RequestHeader.Pragma]: 'no-cache' }
    );
  }

  getEnrollmentsByIncentiveProgID(id: string): Observable<ProgramEnrollmentSigningData[]> {
    return this.http.get<ProgramEnrollmentSigningData[]>(`${this.apiDomain}/api/${this.endpoint}/${id}/enrollments`);
  }

  createIncentiveProgram(incentiveProgram: IncentiveProgramSigning) {
    incentiveProgram.activeFrom = moment(incentiveProgram.activeFrom).startOf('day').utc().toDate();
    incentiveProgram.activeUntil = incentiveProgram.activeUntil ?
      moment(incentiveProgram.activeUntil).endOf('day').utc().toDate() : null;
    const incentiveProgramRequest = encodeTextFields(incentiveProgram);

    return this.httpConcurrencyClient
      .postWithETag<IncentiveProgramSigning>(`${this.apiDomain}/api/${this.endpoint}/create`, incentiveProgramRequest);
  }

  update(id: string, incentiveProgram: IncentiveProgramSigning) {
    incentiveProgram.activeFrom = moment(incentiveProgram.activeFrom).startOf('day').utc().toDate();
    incentiveProgram.activeUntil = incentiveProgram.activeUntil ?
      moment(incentiveProgram.activeUntil).endOf('day').utc().toDate() : null;
    const incentiveProgramRequest = encodeTextFields(incentiveProgram);

    return this.httpConcurrencyClient.putIfMatch<IncentiveProgramSigning>(
      `${this.apiDomain}/api/${this.endpoint}/${id}/update`,
      incentiveProgramRequest,
      id);
  }

  validate(id: string, incentiveProgramFormData: IncentiveProgramSigningValidationDataViewModel) {
    return this.http.post(`${this.apiDomain}/api/${this.endpoint}/${id}/validate`, incentiveProgramFormData);
  }

  activate(id: string) {
    return this.httpConcurrencyClient.putIfMatch(
      `${this.apiDomain}/api/${this.endpoint}/${id}/activate`,
      {},
      id);
  }

  deactivate(id: string, comment: string) {
    const deactivateRequest = {
      ReasonToDeactivate: textEncodeBase64(comment)
    };

    return this.httpConcurrencyClient.putIfMatch(
      `${this.apiDomain}/api/${this.endpoint}/${id}/deactivate`,
      deactivateRequest,
      id);
  }

  getDocumentTypes(): Observable<TemplateDocumentType[]> {
    return this.http.get<TemplateDocumentType[]>(`${this.apiDomain}/api/${this.endpoint}/documenttypes`);
  }

  getCachedDocumentTypes(): Observable<TemplateDocumentType[]> {
    return this.documentTypes$.value
      ? this.documentTypes$.asObservable()
      : this.getDocumentTypes().pipe(
        tap(documentTypes => this.documentTypes$.next(documentTypes))
      );
  }

  getFolders(): Observable<FolderDetails[]> {
    return this.http.get<FolderDetails[]>(`${this.getFullEndpointUrl()}/folders`);
  }

  getFolderTemplates(id: string, folderId: string): Observable<TemplateSummaryList> {
    return this.http.get<TemplateSummaryList>(`${this.getFullEndpointUrl()}/${id}/folders/${folderId}/templates`);
  }

  getLinkedTemplates(id: string): Observable<LinkedTemplate> {
    return this.http.get<LinkedTemplate>(`${this.getFullEndpointUrl()}/${id}/templates`);
  }

  updateTemplates(id: string, templates: TemplateSummary[]) {
    let requestTemplates = mapTemplateSummariesToTemplateSummaryLinkUnlink(templates);
    requestTemplates = this.encodeTextFieldsTemplateSummaryLinkUnlinkRequests(requestTemplates);
    const request: TemplateSummaryLinkUnlinkRequest = {
      templates: requestTemplates
    }

    return this.httpConcurrencyClient.postIfMatch(
      `${this.apiDomain}/api/${this.endpoint}/${id}/templates`,
      request,
      id);
  }

  getTemplateById(id: string, templateId: string): Observable<TemplateDetails> {
    return this.httpConcurrencyClient.getWithETag<TemplateDetails>(
      `${this.getFullEndpointUrl()}/${id}/templates/${templateId}`,
      templateId,
    );
  }

  getTemplateDocuments(id: string, templateId: string) {
    return this.httpConcurrencyClient.getWithETag<TemplateDocumentDetails[]>(
      `${this.getFullEndpointUrl()}/${id}/templates/${templateId}/documents`,
      templateId,
    );
  }

  updateTemplateDocumentTypes(id: string, templateId: string, documents: TemplateDocumentDetails[]) {
    const requestData: PostTemplateDocumentTypesRequest = {
      templateDocuments: mapTemplateDocumentDetailsToTemplateDocumentDocumentTypes(documents),
    }

    return this.httpConcurrencyClient.putIfMatch(
      `${this.getFullEndpointUrl()}/${id}/templates/${templateId}/documenttypes`,
      requestData,
      templateId);
  }

  getTemplateDocumentFileds(documentType: number): Observable<DataFieldDefinition[]> {
    return this.http.get<DataFieldDefinition[]>(`${this.getFullEndpointUrl()}/documenttypes/${documentType}/datafields`);
  }

  addTemplateDocumentFields(id: string, templateId: string, documentId: string): Observable<DocumentFieldsResponse> {
    const route = `${this.getFullEndpointUrl()}/${id}/templates/${templateId}/documents/${documentId}/fields/import`;

    return this.httpConcurrencyClient.postIfMatch<DocumentFieldsResponse>(route, null, templateId);
  }

  validateTemplateDocumentFields(id: string, templateId: string, documentId: string): Observable<DocumentFieldsValidationResponse> {
    const route = `${this.getFullEndpointUrl()}/${id}/templates/${templateId}/documents/${documentId}/fields/validate`;

    return this.httpConcurrencyClient.postIfMatch<DocumentFieldsValidationResponse>(route, null, templateId);
  }

  resync(id: string, templateIds: string[]): Observable<DataFieldDefinition[]> {
    const request = {
      templateIds
    }

    return this.http.post<DataFieldDefinition[]>(`${this.apiDomain}/api/${this.endpoint}/${id}/resync`, request);
  }

  validateReport(compensationTrigger: CompensationTrigger): Observable<IncentiveProgramSigningErrorResponse> {
    return this.http.post<IncentiveProgramSigningErrorResponse>(
      `${this.apiDomain}/api/${this.endpoint}/validatereport`,
      null,
      { params: { compensationTrigger } });
  }

  private encodeTextFieldsTemplateSummaryLinkUnlinkRequests(templates: TemplateSummaryLinkUnlink[]) {
    const templatesCopy: TemplateSummaryLinkUnlink[] = cloneDeep(templates);

    templatesCopy.forEach(template => template.externalTemplateId = textEncodeBase64(template.externalTemplateId));

    return templatesCopy;
  }
}
